import React, { useEffect, useState } from 'react';
import { Employee, EmployeeRole } from '../../types/pro';
import { Modal } from './Modal';
interface EmployeeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Omit to add a new colleague. */
  employee?: Employee;
  onSave: (employee: Omit<Employee, 'id'>) => void;
  onDelete?: () => void;
}
const ROLES: EmployeeRole[] = ['Administrator', 'Projektleder', 'Medarbejder'];

/** Calendar colours, kept distinct enough to tell apart at a glance. */
const COLORS = ['#ea580c', '#0284c7', '#059669', '#7c3aed', '#db2777', '#ca8a04', '#475569'];
interface Draft {
  name: string;
  role: EmployeeRole;
  phone: string;
  email: string;
  color: string;
  hourlyRate: string;
  active: boolean;
}
const blank: Draft = {
  name: '',
  role: 'Medarbejder',
  phone: '',
  email: '',
  color: COLORS[0],
  hourlyRate: '545',
  active: true
};
export function EmployeeFormModal({
  isOpen,
  onClose,
  employee,
  onSave,
  onDelete
}: EmployeeFormModalProps) {
  const [draft, setDraft] = useState<Draft>(blank);
  useEffect(() => {
    if (!isOpen) return;
    setDraft(employee ? {
      name: employee.name,
      role: employee.role,
      phone: employee.phone,
      email: employee.email,
      color: employee.color,
      hourlyRate: String(employee.hourlyRate),
      active: employee.active
    } : blank);
  }, [isOpen, employee]);
  const set = <K extends keyof Draft,>(key: K, value: Draft[K]) => setDraft(current => ({
    ...current,
    [key]: value
  }));
  const canSave = draft.name.trim().length > 0;
  const submit = () => {
    if (!canSave) return;
    onSave({
      name: draft.name.trim(),
      role: draft.role,
      phone: draft.phone.trim(),
      email: draft.email.trim(),
      color: draft.color,
      hourlyRate: parseFloat(draft.hourlyRate) || 0,
      active: draft.active
    });
    onClose();
  };
  const field = 'w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500';
  const label = 'mb-1.5 block text-sm font-medium text-stone-700';
  return <Modal isOpen={isOpen} onClose={onClose} title={employee ? 'Rediger medarbejder' : 'Ny medarbejder'} subtitle={employee ? 'Rollen bestemmer hvad personen må se og ændre' : 'Tilføj en kollega til kalender og timeregistrering'} footer={<>
          {employee && onDelete && <button onClick={() => {
      if (window.confirm(`Fjern ${employee.name}? Registrerede timer og opgaver bliver liggende.`)) {
        onDelete();
        onClose();
      }
    }} className="mr-auto rounded-lg px-4 py-2.5 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50" data-id="element-158" data-mp="k8092gdcf0ag">
              Fjern medarbejder
            </button>}
          <button onClick={onClose} className="rounded-lg border border-stone-200 bg-white px-4 py-2.5 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-100" data-id="element-159" data-mp="29sp8n3kqmi0o">
            Annuller
          </button>
          <button onClick={submit} disabled={!canSave} className="rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:bg-stone-200 disabled:text-stone-400" data-id="element-160" data-mp="20zlx50tnkf8o">
            {employee ? 'Gem ændringer' : 'Tilføj medarbejder'}
          </button>
        </>} data-id="element-157" data-mp="17xkv584smdny">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-161" data-mp="1i215xn3f14kw">
        <label className="block sm:col-span-2" data-id="element-162" data-mp="3ggvtv4o59cfg">
          <span className={label} data-id="element-163" data-mp="3gfkstcm32is1">
            Navn<span className="text-red-500" data-id="element-164" data-mp="3geje8y5uze5k"> *</span>
          </span>
          <input autoFocus value={draft.name} onChange={e => set('name', e.target.value)} className={field} data-id="element-165" data-mp="3cfsgqpxhduhx" />
        </label>
        <label className="block" data-id="element-166" data-mp="3ogikeyjr9nrq">
          <span className={label} data-id="element-167" data-mp="3ofha4wakd5b2">Rolle</span>
          <select value={draft.role} onChange={e => set('role', e.target.value as EmployeeRole)} className={field} data-id="element-168" data-mp="3aikbw9s48lk5">
            {ROLES.map(role => <option key={role} value={role} data-id="element-169" data-mp="30rplqubr11tf">
                {role}
              </option>)}
          </select>
        </label>
        <label className="block" data-id="element-170" data-mp="35k0os61sd3q7">
          <span className={label} data-id="element-171" data-mp="2qu35a3esx27c">Timepris (kr)</span>
          <input type="number" min={0} step={5} value={draft.hourlyRate} onChange={e => set('hourlyRate', e.target.value)} className={`${field} tabular-nums`} data-id="element-172" data-mp="2qve6pwkrxxva" />
        </label>
        <label className="block" data-id="element-173" data-mp="2msq26133fd44">
          <span className={label} data-id="element-174" data-mp="2mroorpyluej8">Telefon</span>
          <input value={draft.phone} onChange={e => set('phone', e.target.value)} inputMode="tel" className={field} data-id="element-175" data-mp="23669fp1lfy9i" />
        </label>
        <label className="block" data-id="element-176" data-mp="2c74e89juw9t5">
          <span className={label} data-id="element-177" data-mp="2c85rmkoch8e1">E-mail</span>
          <input value={draft.email} onChange={e => set('email', e.target.value)} inputMode="email" className={field} data-id="element-178" data-mp="2c4rqffrf3586" />
        </label>

        <fieldset className="sm:col-span-2" data-id="element-179" data-mp="2m37knrlqqulw">
          <legend className={label} data-id="element-180" data-mp="2h6z7gjr3tbhd">Farve i kalenderen</legend>
          <div className="flex flex-wrap gap-2" data-id="element-181" data-mp="2vv3t9wlge34d">
            {COLORS.map(color => <button key={color} type="button" onClick={() => set('color', color)} aria-label={`Vælg farve ${color}`} aria-pressed={draft.color === color} style={{
            backgroundColor: color
          }} className={`h-9 w-9 rounded-full transition-transform ${draft.color === color ? 'ring-2 ring-stone-900 ring-offset-2' : 'hover:scale-105'}`} data-id="element-182" data-mp="2vwwpehyfx4y4" />)}
          </div>
        </fieldset>

        <label className="flex items-center gap-3 rounded-lg border border-stone-200 p-3 sm:col-span-2" data-id="element-183" data-mp="26io1fiyiirkq">
          <input type="checkbox" checked={draft.active} onChange={e => set('active', e.target.checked)} className="h-4 w-4 accent-brand-600" data-id="element-184" data-mp="2bcs7lzzyevkr" />
          <span className="text-sm" data-id="element-185" data-mp="2lay8cea0xvo1">
            <span className="block font-medium text-stone-900" data-id="element-186" data-mp="2q7eyfe0sak3r">Aktiv</span>
            <span className="block text-stone-500" data-id="element-187" data-mp="2u9jndzgaesk2">
              Inaktive medarbejdere kan ikke tildeles nye opgaver.
            </span>
          </span>
        </label>
      </div>
    </Modal>;
}